/**
	* Map tools
	* Handles: zooming tools
	* @module tools/map
*/

// OpenLayers
import OlMap from 'ol/Map';
import View from 'ol/View';
import Layer from 'ol/layer/Layer';
import Source from 'ol/source/Source';
import Feature from 'ol/Feature';
import Geometry from 'ol/geom/Geometry';
import Extent, { applyTransform } from 'ol/extent';
import WKT from 'ol/format/WKT';
import GeoJSON from 'ol/format/GeoJSON';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import html2canvas from 'html2canvas';

// Models
import OpusLayer from 'lib/classes/opuslayer';
import OpusLayerGroup from 'lib/classes/opuslayergroup';

// Tools
import { fetchRequestJson, fetchOptions } from 'lib/utilities/network';
import { updateURLParams } from 'lib/utilities/misc';
import { reverseNormal } from 'lib/utilities/maths';
import { sendMapScreenshot } from 'lib/utilities/communications';
import { transform, getTransform } from 'ol/proj';

// Other
import { serialize } from 'lib/includes/hashed/hash';

/**
	* Zoom map to extent and match to screen dimensions
	*
	* @param {any} extent - Extents
	* @param {OlMap} map - Map to zoom
*/
export function zoomToExtentScreen(extent:any, map:OlMap) {
	const totalSize:number = _dimensions.width + _dimensions.height;
	const p:number = -250 * (totalSize / (700*2));
	
	map.getView().fit(extent, {padding: [p, p, p, p]});
}

/**
	* Zoom map to extent and match to screen dimensions
	*
	* @param {any} extent - Extents
	* @param {number} p - Padding
	* @param {OlMap} map - Map to zoom
*/
export function zoomToExtent(extent:any, p:number, map:OlMap) {
	map.getView().fit(extent, {padding: [p, p, p, p]});
}

/**
	* Get all layer names on a certain map
	*
	* @param {OlMap} map - Map to get layers from
*/
export function getAllLayerCodes(map:OlMap, numeric:boolean = false):string {
	const layerNames:Array<string> = [];
	
	const opusLayerGroups:OpusLayerGroup[] = map.get('opus-layer-groups');	
	
	opusLayerGroups.forEach((opusLayerGroup:OpusLayerGroup) => {
		opusLayerGroup.layers.forEach((opusLayer:OpusLayer) => {
			layerNames.push(!numeric ? opusLayer.id : opusLayer.id.split(':')[1]);
		});
	});
	
	return layerNames.join(',');
}

/**
	* Get all layer names on a certain map that are visible
	*
	* @param {OlMap} map - Map to get layers from
*/
export function getAllLayerCodesVisible(map:OlMap, numeric:boolean = false):string {
	const layerNames:Array<string> = [];
	
	const opusLayerGroups:OpusLayerGroup[] = map.get('opus-layer-groups');	
	
	opusLayerGroups.forEach((opusLayerGroup:OpusLayerGroup) => {
		opusLayerGroup.layers.forEach((opusLayer:OpusLayer) => {
			if(opusLayer.getVisible())
				layerNames.push(!numeric ? opusLayer.id : opusLayer.id.split(':')[1]);
		});
	});
	
	return layerNames.join(',');
}

/**
	* Get OpusLayer by layer code
	*
	* @param {string} code/id of layer
	* @param {OlMap} map - Map to search
*/
export function getOpusLayerByCode(code:string, map:OlMap):OpusLayer {
	let matchLayer:OpusLayer = null;
	
	const opusLayerGroups:OpusLayerGroup[] = map.get('opus-layer-groups');	
	
	opusLayerGroups.forEach((opusLayerGroup:OpusLayerGroup) => {
		opusLayerGroup.layers.forEach((opusLayer:OpusLayer) => {
			if(opusLayer && code == opusLayer.id)
				matchLayer = opusLayer;
		});
	});
	
	return matchLayer;
}

/**
	* Focus on layer
	*
	* @param {Layer} layer - Layer to focus on
	* @param {OlMap} map - Map to focus
*/
export function focusOnLayer(layer:any, map:OlMap) {
	const extent = layer.getExtent();
	
	if(!extent) return;
	
	const p:number = _config.focusFeaturePadding;
	
	map.getView().fit(extent, {padding: [p, p, p, p]});
}

/**
	Get map zoom
	
	@returns {number} Zoom level of map
*/
export function getMapZoom(map:OlMap, rounded:boolean = true):number {
	const zoom:number = map.getView().getZoom();
	
	if(rounded)
		return Math.round(zoom);
	
	return zoom;
}

/**
	* Get base layers
	*
	* @returns
	* Base layers
	* 
	* @param {OlMap} map - Map to get layers from
*/
export function getBaseLayers(map:OlMap):Layer[] {
	const layers:Array<Layer> = [];
	
	map.getLayers().forEach((layer:Layer) => {
		if(layer.get('base'))
			layers.push(layer);
	});
	
	return layers;
}

/**
	* Get opus layers visible
	* 
	* @returns
	* Opus layers that are visible
	* 
	* @param {OlMap} map - Map to get layers from
*/
export function getOpusLayersVisible(map:OlMap):OpusLayer[] {
	const opusLayersVisible:OpusLayer[] = [];
	const opusLayerGroups:OpusLayerGroup[] = map.get('opus-layer-groups');	
	
	opusLayerGroups.forEach((opusLayerGroup:OpusLayerGroup) => {
		opusLayerGroup.layers.forEach((opusLayer:OpusLayer) => {
			if(opusLayer.getVisible()) {
				opusLayersVisible.push(opusLayer);
			}
		});
	});
	
	return opusLayersVisible;
}

/**
	* Get opus layer groups that are collapsed in UI
	* 
	* @returns 
	* {OpusLayerGroup[]} - Layer groups that are visible
	* 
	* @param {OlMap} map - Map to get groups from
*/
export function getOpusLayersCollapsed(map:OlMap):OpusLayerGroup[] {
	const opusGroupsCollapsed:OpusLayerGroup[] = [];
	const opusLayerGroups:OpusLayerGroup[] = map.get('opus-layer-groups');	
	
	opusLayerGroups.forEach((opusLayerGroup:OpusLayerGroup) => {
		if(opusLayerGroup.collapsed)
			opusGroupsCollapsed.push(opusLayerGroup);
	});
	
	return opusGroupsCollapsed;
}

/**
	* Get all opus groups collapsed
	* 
	* @returns
	* Are all the opus groups collapsed?
	* 
	* @param {OlMap} map - Map to get groups from
*/
export function getAllOpusLayersCollapsed(map:OlMap):boolean {
	let result:boolean = true;
	
	const opusLayerGroups:OpusLayerGroup[] = map.get('opus-layer-groups');	
	
	opusLayerGroups.forEach((opusLayerGroup:OpusLayerGroup) => {
		if(!opusLayerGroup.collapsed)
			result = false;
	});
	
	return result;
}

/**
	* Get all opus layers visible
	* 
	* @returns
	* Are all the opus layers visible?
	* 
	* @param {OlMap} map - Map to get layers from
*/
export function getAllOpusLayersVisible(map:OlMap):boolean {
	let result:boolean = true;
	
	const opusLayerGroups:OpusLayerGroup[] = map.get('opus-layer-groups');	
	
	opusLayerGroups.forEach((opusLayerGroup:OpusLayerGroup) => {
		opusLayerGroup.layers.forEach((opusLayer:OpusLayer) => {
			if(opusLayer._visibleInLegendOMV && !opusLayer.getVisible()) {
				result = false;
			}
		});
	});
	
	return result;
}

/**
	* Get visible base layers
	*
	* @returns
	* Base layers that are visible
	* 
	* @param {OlMap} map - Map to get layers from
*/
export function getVisibleBaseLayers(map:OlMap):Layer[] {
	const layers:Array<Layer> = [];
	
	map.getLayers().forEach((layer:Layer) => {
		if(layer.get('base') && layer.getVisible())
			layers.push(layer);
	});
	
	return layers;
}

/**
	* Get default visible base layer codes
	*
	* @returns
	* Base layer codes of base layers that are default
	* 
	* @param {OlMap} map - Map to get layers from
*/
export function getDefaultBaseLayerCodes(map:OlMap):string[] {
	const codes:string[] = [];
	
	map.getLayers().forEach((layer:Layer) => {
		if(layer.get('base') && layer.get('default-layer') && layer.get('code'))
			codes.push(layer.get('code'));
	});
	
	return codes;
}

/**
	* Get visible base layer codes
	*
	* @returns
	* Base layer codes of layers that are visible
	* 
	* @param {OlMap} map - Map to get layers from
*/
export function getVisibleBaseLayerCodes(map:OlMap):string[] {
	const codes:string[] = [];
	
	map.getLayers().forEach((layer:Layer) => {
		if(layer.get('base') && layer.getVisible() && layer.get('code'))
			codes.push(layer.get('code'));
	});
	
	return codes;
}

/**
	* Get visible layer codes
	*
	* @returns
	* Map layer codes of layers that are visible
	* 
	* @param {OlMap} map - Map to get layers from
*/
export function getVisibleLayerCodes(map:OlMap, options:any = {}):string[] {
	const layersShow:OpusLayer[] = [];
	const opusLayerGroups:OpusLayerGroup[] = map.get('opus-layer-groups');	
	
	// Options
	const excludes:string[] = options.exclude ?? [];
	
	opusLayerGroups.forEach((opusLayerGroup:OpusLayerGroup) => {
		opusLayerGroup.layers.forEach((opusLayer:OpusLayer) => {
			if(opusLayer.getVisible())
				layersShow.push(opusLayer);
		});
	});
	
	/*
		Sort the layers by cartographicOrder property
	*/
	layersShow.sort((a:OpusLayer, b:OpusLayer) => {
		if(a.cartographicOrder > b.cartographicOrder) return 1;
		if(a.cartographicOrder < b.cartographicOrder) return -1;
		return 0;
	});
	
	const codes:string[] = [];
	
	layersShow.forEach((layer:OpusLayer) => {
		let skip:boolean = false;
		
		excludes.forEach((exclude:string) => {
			if(layer.id.includes(exclude))
				skip = true;
		});
		
		if(skip)
			return;
		
		codes.push(layer.id);
	});
	
	return codes;
}

/**
	* Get collapsed layer group ids
	*
	* @returns
	* Ids of layer groups that are collapsed
	* 
	* @param {OlMap} map - Map to get layers from
*/
export function getCollapsedLayerGroupIds(map:OlMap):number[] {
	const ids:number[] = [];
	const opusLayerGroups:OpusLayerGroup[] = map.get('opus-layer-groups');	
	
	opusLayerGroups.forEach((opusLayerGroup:OpusLayerGroup) => {
		if(opusLayerGroup.collapsed)
			ids.push(opusLayerGroup.id);
	});
	
	return ids;
}

/**
	* Synchronise map layers by codes
	* 
	* @param {String[]} codes - Layer codes
	* @param {OlMap} map - Map to sync
*/
export function syncLayers(codes:string[], map:OlMap) {
	const opusLayerGroups:OpusLayerGroup[] = map.get('opus-layer-groups');
	
	// Make all opus layers invisible
	opusLayerGroups.forEach((opusLayerGroup:OpusLayerGroup) => {
		opusLayerGroup.layers.forEach((opusLayer:OpusLayer) => {
			opusLayer.setVisible(false, false);
		});
	});
	
	for(let code of codes) {
		// Sync the base layers
		map.getLayers().forEach((layer:Layer) => {
			if(layer.get('code') == code && layer.get('base')) {
				const baseLayers:Layer[] = getBaseLayers(map);
				
				_events.invoke('enable-base-layer', layer);
				_config.defaultBaseLayerIndex = baseLayers.indexOf(layer);
			}
		});
		
		// Sync the opus layers
		opusLayerGroups.forEach((opusLayerGroup:OpusLayerGroup) => {
			opusLayerGroup.layers.forEach((opusLayer:OpusLayer) => {
				if(code == opusLayer.id) {
					opusLayer.setVisible(true, false);
				}
			});
		});
	}
	
	// Don't ask why, but we have to do it or else the above fails to be saved to the very same object
	map.set('opus-layer-groups', [...opusLayerGroups]);
	
	// Call, even if no changes
	_events.invoke('layer-toggled');
	
	// For anywhere else that is interested in syncing opus layers by their codes (e.g. RasterImageTool)
	_events.invoke('sync-layers', codes, map);
	
	// Store sync layer codes
	map.set('sync-layer-codes', codes);
}

/**
	* Request wkt feature
*/
export function requestWktFeature(featureId:number, map:OlMap) {
	if(!featureId) return;
	
	const mapConfig:any = map.get('opus-config');
	const layerId:number = Number(getVisibleLayerCodes(map, {exclude: ['annotation']})[0]?.split(':')?.[1]) || 0;
	
	if(!layerId) {
		console.log(`Invalid layer ID for WKT feature request: ${layerId}`);
		return;
	}
	
	const url:string = updateURLParams(mapConfig.getFeatureWktUrl, {
		id: featureId,
		siteid: '',
		layerid: layerId
	});
	
	// Loading the feature wkt api request
	_ui.showLoading();
	
	fetchRequestJson(url, fetchOptions)
	.then((response:any) => {
		const format:WKT = new WKT();
		
		const boundsFeature:Feature<Geometry> = format.readFeature(response.bounds, {
			dataProjection: mapConfig.epsgCode,
			featureProjection: mapConfig.epsgCodeView,
		});
		const highlightFeature:Feature<Geometry> = format.readFeature(response.geom, {
			dataProjection: mapConfig.epsgCode,
			featureProjection: mapConfig.epsgCodeView,
		});
		
		highlightFeature.set('highlight', true);
		
		const extent:any = boundsFeature.getGeometry().getExtent();
		const sitesLayer:VectorLayer<VectorSource> = map.get('sites-layer');
			
		sitesLayer.getSource().addFeature(highlightFeature);
		
		zoomToExtent(extent, 5, map);
		
		// Get rid of the feature part from URL as soon as the job is done
		_events.invoke('hashed-update', {feature: null});
		
		_ui.hideLoading();
	})
	.catch((error:any) => {
		_ui.hideLoading();
		
		if(error == '404') {
			_ui.showError(`Site not found`);
		}else{
			_ui.showError(`Site not found. If you believe this to be a mistake please report it.`);
		}
		console.log(`Error handling feature wkt data response: ${error}`);
	});
}

/**
	* Replace hash values
	* 
	* @param {any} sync - Sync object
	* @param {any} values - New values
	* 
	* @remarks
	* Removes any un-specified values from the url hash, and then 
	* injects the new ones in
*/
export function replaceHashValues(sync:any, values:any) {
	const store = sync.instance.getStore();
	const newValues:any = {};
	
	for(let param in store._values) {
		if(sync.params.indexOf(param) > -1 && store._values[param] != undefined)
			newValues[param] = store._values[param];
	}
	
	store._values = newValues;
	
	for(let i in values) {
		store._values[i] = values[i];
	}
	
	location.hash = serialize(store._values);
	
	// Hard reset
	sync.registerHashed();
}

/**
	* Get the location and site features as defined in config
	* 
	* @param {OlMap} map - Map to read from
*/
export function getLocationAndSiteFeatures(map:OlMap) {
	const mapConfig:any = map.get('opus-config');
	
	// GeoJSON format
	const format:GeoJSON = new GeoJSON({ featureProjection: mapConfig.epsgCodeView });
	
	// Features array to contain all the locations/sites
	const features:Feature<Geometry>[] = [];
	
	if(mapConfig.locations?.features?.length > 0) {
		for(let geoJson of mapConfig.locations.features) {
			const location:Feature<Geometry> = format.readFeature(geoJson);
			
			features.push(location);
		}
	}
	if(mapConfig.sites?.features?.length > 0) {
		for(let geoJson of mapConfig.sites.features) {
			const location:Feature<Geometry> = format.readFeature(geoJson);
			
			features.push(location);
		}
	}
	
	return features;
}

/**
	* Returns snap tolerance multiplier based on screen dimensions
	* 
	* @remarks
	* Smaller screen = mobile device so increase snapping tolerance
	* to make it easier to draw
*/
export function getSnapToleranceMultiplier():number {
	if(_dimensions.width <= 1080 || _dimensions.height <= 280)
		return 1.8;
		
	return 1;
}

/**
	* Get map co-ordinates in lat/long
	*
	* @param {OlMap} map - Map to read co-ordinates from
	* 
	* @returns {Array<number>} - Co-ordinates
*/
export function getMapCoords(map:OlMap) {
	const mapConfig:any = map.get('opus-config');
	
	return transform(
		map.getView().getCenter(),
		mapConfig.epsgCodeView,
		'EPSG:4326' // lat long
	);
}

/**
	* Get extent from pixel width/height
	* 
	* @param {OlMap} map - Map to create extent from
	* @param {number} width - Width in pixels
	* @param {number} height - Height in pixels
	* 
	* @returns {Extent} extent
*/
export function getExtentFromPixels(map:OlMap, width:number, height:number, epsgCode:string) {
	const view:View = map.getView();
	const resolution:number = view.getResolution();
	const center:Array<number> = view.getCenter();
	
	const minX:number = center[0] - (width / 2) * resolution;
	const minY:number = center[1] - (height / 2) * resolution;
	const maxX:number = center[0] + (width / 2) * resolution;
	const maxY:number = center[1] + (height / 2) * resolution;
	
	return applyTransform([minX, minY, maxX, maxY], getTransform(view.getProjection(), epsgCode));
}

/**
	* Get are any raster features on raster feature source
	* 
	* @returns {boolean} result
*/
export function getIsRasterFeatureOnMap(map:OlMap):boolean {
	const featureLayer:VectorLayer<VectorSource> = map.get('raster-feature-layer');
	const featureSource:VectorSource = featureLayer.getSource();
	
	let found:boolean = false;
	
	featureSource.getFeatures().forEach((feature:Feature<Geometry>) => {
		if(feature.get('rasterimg') && !feature.get('collapsed'))
			found = true;
	});
	
	return found;
}

/**
	* Get are any annotations features on annotation feature source
	* 
	* @returns {boolean} result
*/
export function getIsAnnotationFeatureOnMap(map:OlMap):boolean {
	const featureLayer:VectorLayer<VectorSource> = map.get('annotation-layer');
	const featureSource:VectorSource = featureLayer.getSource();
	
	let found:boolean = false;
	
	featureSource.getFeatures().forEach((feature:Feature<Geometry>) => {
		if(feature.get('annotation') && !feature.get('collapsed'))
			found = true;
	});
	
	return found;
}

/**
	* Get ids from opus layer array
	* 
	* @param {OpusLayer[]} layers
	* 
	* @returns {string[]} ids
*/
export function getOpusLayerIds(opusLayers:OpusLayer[]):string[] {
	const ids:string[] = [];
	
	opusLayers.forEach((layer:OpusLayer) => {
		ids.push(layer.id);
	});
	
	return ids;
}

/**
	* Take screenshot of map
	* 
	* @param {OlMap} OpenLayers Map instance
 */
export async function takeScreenshot(olMap:OlMap) {
	try {
		olMap.render();
		olMap.once('rendercomplete', () => {
			captureAndResize(
				olMap.getViewport(),
				_config.screenshotWidth, 
				_config.screenshotHeight
			)
			.then((blob:string) => sendMapScreenshot(blob, _config.clientId, _config.mapId))
			.catch(e => console.error(e));
		});
	} catch (e) {
		console.error(e);
	}
}

/**
	* Capture and resize
	* 
	* @param {ref} Rect dom ref
	* @param {number} target width
	* @param {number} target height
 */
export async function captureAndResize(viewport:HTMLElement, targetWidth:number, targetHeight:number) {
	if (!viewport) {
		throw new Error('Invalid viewport: Element is not mounted or available.');
	}

	// Step 1: Capture the original canvas using html2canvas
	const originalCanvas = await html2canvas(viewport);
	
	// Step 2: Create a new canvas for resizing
	const resizedCanvas = document.createElement('canvas');
	resizedCanvas.width = targetWidth;
	resizedCanvas.height = targetHeight;
	
	const ctx = resizedCanvas.getContext('2d');
	
	// Step 3: Draw the original image onto the new canvas with resizing
	const img = new Image();
	return new Promise((resolve, reject) => {
		img.onload = () => {
			ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
	
			// Step 4: Convert the resized canvas to a Base64 string
			const base64String = resizedCanvas.toDataURL('image/png');
			resolve(base64String); // Pass the Base64 string to the resolve
		};
	
		img.onerror = () => reject(new Error('Failed to load the image.'));
		img.src = originalCanvas.toDataURL(); // Load the original canvas as an image source
	});
}